import { API } from 'aws-amplify'
import Products from "./products"
import { getProductFrom, getSkuPrice, skuPrice, useDailyInsurance, isWeekday, skuIdPrice, objEmpty, isHourly, getPassTypeFrom, dateEquivalentInCPH, attempt } from "./utils"
import AuthenticatedAPI from './authenticatedAPI'

const BookingLib = {

  totalBookingSum(booking, skus, overrideHours, email) {
    const eligible = booking.people
      ? booking.people
        .filter(person => (!!person.passType || (person.membership && person.membership.type)))
      : []
    if (eligible.length === 0) { return 0 }

    return eligible
      .map(person => BookingLib.totalPersonPrice(person, booking, skus, overrideHours, email) + BookingLib.couponValue(person, booking, skus, overrideHours))
      .reduce((total, num) => total + num)
  },

  totalPersonPrice(person, booking, skus, overrideHours, email) {
    const passType = person.passType || (person.membership && person.membership.type)
    const product = getProductFrom(passType)
    const ageCategory = person.ageCategory || (person.membership && person.membership.ageCategory)
    const hoursCount = BookingLib.calculateHoursCount(booking.whenStartTime, booking.whenEndTime)

    return this.totalPassPrice(person, product, booking, skus, overrideHours)
      + this.totalOtherRelatedPrice(person, ageCategory, hoursCount, passType, skus)
      + this.voucherValue(person, booking, skus, overrideHours, email)
  },

  discountedPrice(price, skuId, discountedSkus, discountAmount, discountType) {
    if (discountedSkus && discountedSkus.length > 0) {
      const skusList = discountedSkus.map(obj => obj.skuId)
      if (!skusList.includes(skuId)) {
        return price
      }
      if (discountType && discountType === 2) {
        return price * (1 - (discountAmount / 100))
      }
      return price - discountAmount
    }
    return price
  },

  totalOtherRelatedPrice(person, ageCategory, hoursCount, passType, skus, discountedSkus, discountAmount, discountType) {
    return this.instructionPrice(person, skus, discountedSkus, discountAmount, discountType)
      // + this.insuranceMandatoryPrice(person, skus, discountedSkus, discountAmount, discountType)
      + this.insuranceCancellationPrice(person, skus, discountedSkus, discountAmount, discountType)
      + this.rentalPrice(person, skus, discountedSkus, discountAmount, discountType)
      + this.replacementCardPrice(person, skus, discountedSkus, discountAmount, discountType)
      + (this.isEligibleForVolumeDiscount(passType, hoursCount) ? this.volumeDiscount(ageCategory) : 0)
  },

  totalPassPrice(person, product, booking, skus, overrideHours) {
    // Claculate days which are considered special weekeends from override hours array
    const specialWeekends = overrideHours.filter(d => d.isWeekend)

    if (!product || !booking.whenStartTime || !booking.whenEndTime || !skus) { return 0 }
    const hoursCount = BookingLib.calculateHoursCount(booking.whenStartTime, booking.whenEndTime)
    const passPrice = (!person.membership
      || (person.membership && person.passType && person.membership.type !== person.passType)
    ) && skuPrice(product, null, skus)
    return (passPrice || 0) + this.hourPriceOnDay(product, booking, skus, specialWeekends) * hoursCount
  },

  hourPriceOnDay(product, booking, skus, specialWeekends) {
    return isWeekday(new Date(booking.whenStartTime), specialWeekends)
      ? skuPrice(product, 'hourRateWeekdaysSkuId', skus) || 0
      : skuPrice(product, 'hourRateWeekendsSkuId', skus) || 0
  },
  isWeekdayEqualToWeekendPrice(product, skus) {
    return skuPrice(product, 'hourRateWeekdaysSkuId', skus) === 
        skuPrice(product, 'hourRateWeekendsSkuId', skus)
  },

  instructionPrice(person, skus, discountedSkus, discountAmount, discountType) {
    if (!person.instruction) { return 0 }
    const { skuId } = person.instruction
    return !skuId ? 0 : BookingLib.discountedPrice(skuIdPrice(skuId, skus), skuId, discountedSkus, discountAmount, discountType)
  },

  rentalPrice(person, skus, discountedSkus, discountAmount, discountType) {
    if (!(person.rental && !objEmpty(person.rental.equipment))) { return 0 }

    const amounts = Object.keys(person.rental.equipment).map(type =>
      person.rental.equipment[type]
        ? BookingLib.discountedPrice(skuIdPrice(person.rental.equipment[type], skus), person.rental.equipment[type], discountedSkus, discountAmount, discountType)
        : 0
    )
    if (person.rental.insurance) {
      const rentalInsuranceSkuId = getProductFrom('insurance').rentalSkuId
      amounts.push(BookingLib.discountedPrice(getSkuPrice('insurance', 'rentalSkuId', skus), rentalInsuranceSkuId, discountedSkus, discountAmount, discountType))
    }
    return amounts.reduce((total, num) => total + num)
  },

  // insuranceMandatoryPrice(person, skus, discountedSkus, discountAmount, discountType) {
  //   if (person.membership && !useDailyInsurance(person.membership.type)) {
  //     return 0
  //   }

  //   const skuIdToUse = useDailyInsurance(person.passType || person.membership.type)
  //     ? 'mandatoryDailySkuId'
  //     : 'mandatoryYearlySkuId'
  //   const skuId = getProductFrom('insurance')[skuIdToUse]
  //   return BookingLib.discountedPrice(getSkuPrice('insurance', [skuIdToUse], skus), skuId, discountedSkus, discountAmount, discountType)
  // },

  insuranceCancellationPrice(person, skus, discountedSkus, discountAmount, discountType) {
    if (!person.insuranceCancellation) { return 0 }
    const skuId = Products.find(p => p.id === 'insurance').cancellationSkuId
    const price = skus.find(s => s.id === skuId).price / 100
    return BookingLib.discountedPrice(price, skuId, discountedSkus, discountAmount, discountType)
  },

  replacementCardPrice(person, skus, discountedSkus, discountAmount, discountType) {
    if (!person.replacementCard) { return 0 }
    const skuId = getProductFrom('misc').replacementCardSkuId
    return BookingLib.discountedPrice(skuIdPrice(skuId, skus), skuId, discountedSkus, discountAmount, discountType)
  },

  insuranceRentalPrice(person, skus) {
    if (!person.rental || !person.rental.insurance) { return 0 }
    return skus.find(s => s.id === Products.find(p => p.id === 'insurance').rentalSkuId).price / 100
  },

  voucherValue(person, booking, skus, overrideHours, email) {
    const membershipType = person.membership && person.membership.type
    const passType = person.passType || membershipType
    const ageCategory = person.ageCategory || (person.membership && person.membership.ageCategory)

    if (!passType || !person.voucher || !person.appliedVoucher || person.couponInfo) { return 0 }

    // Claculate days which are considered special weekeends from override hours array
    const specialWeekends = overrideHours.filter(d => d.isWeekend)

    // Preparation
    let price = 0
    const voucherObj = person.appliedVoucher
    const isPercentageDiscount = voucherObj.voucherTypeId === 2
    const isRestricted = voucherObj.validFor && voucherObj.validFor.length > 0
    const restrictedToPassTypes = voucherObj.validFor && voucherObj.validFor.map(obj => getPassTypeFrom(obj.skuId))
    const hourlyKeyToUse = isWeekday(new Date(booking.whenStartTime), specialWeekends) ? 'hourRateWeekdaysSkuId' : 'hourRateWeekendsSkuId'
    const hourlySku = getProductFrom(passType)[hourlyKeyToUse]
    const hoursCount = BookingLib.calculateHoursCount(booking.whenStartTime, booking.whenEndTime)
    const calcPercentPrice = !passType ? 0 : getSkuPrice(passType, 'skuId', skus) * (voucherObj.amount)
    const calcTimePrice = (key, discountedQuantity) => {
      const hoursAmount = (!discountedQuantity || (discountedQuantity > hoursCount)) ? hoursCount : discountedQuantity
      return hoursAmount * skuPrice(getProductFrom(passType), key, skus) * (voucherObj.amount)
    }
    const hourPriceIsDiscounted = voucherObj.validFor && voucherObj.validFor.map(obj => obj.skuId).includes(hourlySku)
    const discountedHourlySku = voucherObj.validFor.find(obj => obj.skuId === hourlySku)
    const discountedQuantity = voucherObj.validFor && discountedHourlySku && discountedHourlySku.quantity

    // Custom for coorporate customers that have bought specifically for their employees
    if (isCustomVoucherAndInvalid(person.voucher, email)) {
      return 0
    }

    // Main logic
    if (isPercentageDiscount) {

      // Pass discount
      if (!isRestricted || restrictedToPassTypes.includes(person.passType)) {
        price = calcPercentPrice
      }

      // Time booked
      if (!isRestricted || hourPriceIsDiscounted) {
        price += calcTimePrice(hourlyKeyToUse, discountedQuantity)
      }

      // Other items, like insurance, rentals etc.
      const priceWithoutDiscount = this.totalOtherRelatedPrice(person, ageCategory, hoursCount, passType, skus)
      if (isRestricted) {
        const priceWithDiscount = this.totalOtherRelatedPrice(person, ageCategory, hoursCount, passType, skus, voucherObj.validFor, voucherObj.amount, voucherObj.voucherTypeId)
        price += (priceWithoutDiscount - priceWithDiscount) * 100
      } else {
        price += priceWithoutDiscount * voucherObj.amount
      }

    } else if (voucherObj.amount) {
      // Cash discount
      if (!isRestricted || discountedHourlySku || restrictedToPassTypes.includes(person.passType)) {
        price = voucherObj.amount
      }
    }
    return Math.round((price / 100) * -1)
  },

  couponValue(person, booking, skus, overrideHours) {

    if (!person.couponInfo) { return 0 }
    const exactValue = person.couponInfo.percent_off / 100 * BookingLib.totalPersonPrice(person, booking, skus, overrideHours) * -1
    return Math.round(exactValue)
  },

  timeIsOutOfBounds(endTime, workingHours, overrideHours) {
    if (endTime) {
      const endTimeValue = new Date(endTime)
      const maxEndTime = new Date(endTime)
      let closingHour

      const dateToCompare = new Date(endTimeValue.setHours(0, 0, 0, 0))

      // Check if today exist in overrides
      const overridesDates = overrideHours.map(override => ({
        date: new Date(override.date),
        timeFrom: override.timeFrom,
        timeTo: override.timeTo,
        isClosed: override.isClosed
      })).filter(override => override.date.getTime() === dateToCompare.getTime())

      // There is override for this day
      if (overridesDates.length !== 0) {
        closingHour = parseInt(overridesDates[0].timeTo, 10)
      } else {
        // Get normal working hours - Get day of week
        const dayOfWeek = endTimeValue.getDay()

        // Get working hours, for that selected date 
        const dayWorkingHours = workingHours[dayOfWeek]
        closingHour = dayWorkingHours ? parseInt(dayWorkingHours.to, 10) : 0
        // const minutes = endTimeValue.getMinutes()

      }

      maxEndTime.setHours(closingHour)
      maxEndTime.setMinutes(0)

      return (endTime > maxEndTime)
    }
    return false
  },

  calculateHoursCount(startTime, endTime) {
    return Math.abs(new Date(endTime) - new Date(startTime)) / 36e5
  },

  async fetchSkus() {
    return API.post('cphill', '/stripe/proxy', {
      body: {
        endpoint: 'skus.list'
      }
    })
    // const limit = 100
    // const skusPromise = startingAfterId => API.post('cphill', '/stripe/proxy', {
    //   body: {
    //     endpoint: 'skus.list',
    //     parameters: {
    //       limit,
    //       ...(startingAfterId && {starting_after: startingAfterId})
    //     }
    //   }
    // })
    // const skus1 = await skusPromise(null)
    // let skus2 = {}
    // if (skus1.data.length === limit) {
    //   const startingAfterId = skus1.data[limit-1].id
    //   skus2 = await skusPromise(startingAfterId)
    // }
    // const skusTotal = [...skus1.data, ...(!objEmpty(skus2) ? skus2.data : [])]
    // if (skusTotal.length === limit * 2) {
    //   Sentry.captureException("IMPORTANT: SKU limit reached!!")
    // }
    // return Promise.resolve(skusTotal);
  },

  fetchUserInfo(user) {
    return API.get('cphill', '/users', {
      headers: {
        Authorization: user.signInUserSession.idToken.jwtToken
      }
    })
  },

  fetchUserBookings(user) {
    return API.get('cphill', '/users/bookings', {
      headers: {
        Authorization: user.signInUserSession.idToken.jwtToken
      }
    })
  },

  async atUserBookingsCapacity() {
    try {
      const [userBookings, userInfo] = await Promise.all([
        AuthenticatedAPI.get('cphill', '/users/bookings'),
        AuthenticatedAPI.get('cphill', '/users')
      ])
      const activeMembership = userInfo.memberships.filter(m => m.isBookersOwn && m.status === null).pop()

      if (!isHourly(activeMembership.type) && userBookings.length >= 2) {
        return true
      }
    } catch (e) { /* do nothing */ }
    return false
  },

  // Get day of week (SUN=0, MON=1, ...., SAT=6 )
  getDayOfWeek(day, month, year) {
    // make date from selected date values
    const startTimeAsDate = new Date(year, month, day)
    return startTimeAsDate.getDay()
  },

  // Fetch settings - can be used to fecth any setting value from DB
  async getSettings(name) {
    return API.get('cphill', `/settings?name=${name}`, {})
  },

  // Fetch settings - can be used to fecth any setting value from DB
  async getOverrides() {
    return API.get('cphill', '/overrideHours', {})
  },

  mapWeekStartToSunday(workingHours) {
    const lastItem = workingHours[6]
    workingHours.pop()
    const wh = [lastItem, ...workingHours]

    return wh
  },

  isEligibleForVolumeDiscount: (passType, hoursCount) => (
    isHourly(passType) && hoursCount >= 3
  ),

  // TODO: Make discount dynamic
  volumeDiscount: ageCategory => (ageCategory === 'adult' && -100) || (ageCategory === 'child' && -65) || 0,

  currentMembership: (participant, userData) => {
    let membership = userData.memberships.find(m => m.id === participant.membershipId)

    const recursiveFinder = parentMembership => {
      const childMembership = userData.memberships
        .find(m => m.id === parentMembership.replacedByMembershipId)

      return childMembership.status !== 'REPLACED' // !childMembership.status || childMembership.status === 'EXPIRED'
        ? childMembership
        : recursiveFinder(childMembership)
    }

    if (membership.replacedByMembershipId) {
      membership = recursiveFinder(membership)
    }
    return membership
  },

  fetchParticipants: async () => {
    const [participants, userData] = await Promise.all([
      AuthenticatedAPI.get('cphill', '/participants'),
      AuthenticatedAPI.get('cphill', '/users')
    ])
    
    participants
      .filter(participant => participant.membershipId)
      .forEach((participant, index) => {
        participants[index].membership = BookingLib.currentMembership(participant, userData)
      })
    return participants.filter(p => p.membership && !p.membership.isBookersOwn)
  },

  // Check if two dates are at the same day
  sameDay(d1, d2) {
    try {
      const date1 = new Date(d1)
      const date2 = new Date(d2)
      return date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate();
    } catch (err) { /* Do nothing */ }
    return false
  }
}

// Is done this way, because we used to just export default BookingLib
// - then we started exporting the single functions later
export const {
  calculateHoursCount,
  fetchUserInfo,
  fetchSkus,
  isEligibleForVolumeDiscount,
  volumeDiscount,
  atUserBookingsCapacity,
  fetchParticipants,
  hourPriceOnDay,
  insuranceMandatoryPrice,
  insuranceCancellationPrice,
  insuranceRentalPrice,
  replacementCardPrice,
  couponValue,
  currentMembership, 
  isWeekdayEqualToWeekendPrice
} = BookingLib

export const postStripeCustomer = (user, userInfo, token) => (
  API.post("cphill", "/stripe/proxy", {
    body: {
      endpoint: 'customers.create',
      parameters: {
        name: `${userInfo.firstName} ${userInfo.lastName}`,
        email: user.attributes.email,
        source: token.id
      }
    }
  })
)

// eslint-disable-next-line camelcase
export const postStripeOrder = (user, customer, items, idempotency_key) => (
  API.post('cphill', '/stripe/proxy', {
    body: {
      endpoint: 'orders.create',
      parameters: {
        currency: 'dkk',
        items,
        email: user.attributes.email,
        ...(customer && { customer })
      },
      options: {
        idempotency_key
      }
    }
  })
)

export const putStripeOrder = (id, parameters) => (
  API.post('cphill', '/stripe/proxy', {
    body: {
      endpoint: 'orders.update',
      parameters: { id, ...parameters }
    }
  })
)

export const postStripeOrderPayment = (stripeOrder, stripeCustomerId, bookingId) => (
  API.post('cphill', '/stripe/proxy', {
    body: {
      endpoint: 'orders.pay',
      parameters: {
        id: stripeOrder.id,
        // source: token.id
        customer: stripeCustomerId,
        metadata: {
          bookingId
        }
      }
    }
  })
)

export const postBooking = (booking, bookingOrders, stripeOrder, skus, intl, overrideHours, email) => {
  // Get equivalent time without timezone in order to make sure dates are correct on danish ground
  const [dateStart, dateEnd] = [booking.whenStartTime, booking.whenEndTime]
    .map(d => dateEquivalentInCPH(new Date(d)).toISOString())

  return AuthenticatedAPI.post('cphill', '/bookings', {
    dateStart,
    dateEnd,
    stripeOrderId: stripeOrder.id,
    metaData: {
      localizedAmount: `${intl.formatNumber(BookingLib.totalBookingSum(booking, skus, overrideHours, email), { minimumFractionDigits: 2 })} DKK`,
      localizedVAT: `${intl.formatNumber(BookingLib.totalBookingSum(booking, skus, overrideHours, email) / 5.0, { minimumFractionDigits: 2 })} DKK`
    },
    bookingOrders,
    lang: intl.locale
  })
}

export const putReleaseReservation = (booking, bookingResult) => (
  API.put('cphill', '/reservations', {
    body: {
      uuid: booking.reservation.uuid,
      bookingId: bookingResult.id
    }
  })
)

export const postInvitation = (person, bookingResult) => (
  AuthenticatedAPI.post('cphill', '/invitations', {
    email: person.invite.email,
    note: person.invite.note,
    bookingId: bookingResult.id
  })
)

export const updatePaymentIntent = (paymentIntentId, stripeCustomerId, metadata) => (
  API.post('cphill', '/stripe/proxy', {
    body: {
      endpoint: 'paymentIntents.update',
      parameters: {
        id: paymentIntentId,
        ...(stripeCustomerId && { customer: stripeCustomerId }),
        metadata
      }
    }
  })
)

export const spoofPaymentWebhook = (bookingResult, stripeOrder) => (
  API.post('cphill', '/stripe/webhook', {
    body: {
      type: 'payment_intent.not_required',
      data: {
        object: {
          id: 'NO_CHARGE',
          metadata: {
            bookingId: bookingResult.id,
            order_id: stripeOrder.id
          }
        }
      }
    }
  })
)

export const fixLegacyData = async booking => {
  const { participants, memberships } = await AuthenticatedAPI.get('cphill', '/users')

  // If missing participants
  if (booking.bookingOrders && booking.bookingOrders.length > 1
    && (!participants || participants.length === 0)
  ) {
    await Promise.all([
      booking.bookingOrders.map(order => AuthenticatedAPI.post('cphill', '/participants', {
        firstName: order.person.firstName,
        lastName: order.person.lastName,
        membershipId: order.membershipId
      }))
    ])
  }

  const promisesToKeep = booking.bookingOrders.filter(order => (!order.membershipId || !order.axess) && order.person).map(order => {
    const membershipId = order.person.isBooker
      ? memberships && (memberships.find(m => m.isBookersOwn) || {}).id
      : (participants.find(p => p.firstName === order.person.firstName && p.lastName === order.person.lastName) || {}).membershipId

    return AuthenticatedAPI.put('cphill', `/bookings/${booking.id}/orders/${order.id}`, { membershipId })
  })
  await Promise.all(promisesToKeep)
}

export const prepaidTicketStatus = (axessOrderNo, journalNo) => (
  API.get("cphill", `/axess/prepaidTicketStatus/${axessOrderNo}/${journalNo}`)
)

export const isCustomVoucherAndInvalid = (voucherCode, email) => {
  if (!voucherCode || !email) {
    return false
  }
  const allowedDomains = ["@maersk.com", "@sealandmaersk.com", "@twill.net"]
  const isCustomVoucher = (voucherCode === 'MaerskSki')
  const invalid = !allowedDomains.some(domain => attempt(() => email.toLowerCase().includes(domain)))

  return (isCustomVoucher && invalid)
}

export default BookingLib